import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar, Dialog, Toast, Tag, Skeleton, PullRefresh } from 'react-vant';
import { ArrowLeft, Passed, Close, ClockO, Cross, LabelO } from '@react-vant/icons';
import { getMyRegistrations, cancelRegistration } from '../../api/club';
import dayjs from 'dayjs';

const MyRegistrations = () => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyRegistrations();
  }, []);

  const fetchMyRegistrations = async () => {
    try {
      setLoading(true);
      const res = await getMyRegistrations({ page: 1, page_size: 50 });
      setRegistrations(res.items || []);
    } catch (error) {
      Toast.fail('获取记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (id) => {
    Dialog.confirm({
      title: '取消报名',
      message: '确定要取消该社团的报名吗？取消后名额将释放给候补同学，操作不可逆。',
      onConfirm: async () => {
        try {
          await cancelRegistration(id);
          Toast.success('取消成功');
          fetchMyRegistrations();
        } catch (error) {
          Toast.fail(error.response?.data?.detail || '取消失败');
        }
      }
    });
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'confirmed':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#00b578', background: '#ecfdf5', padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
            <Passed size={14} /> 报名成功
          </div>
        );
      case 'waitlisted':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#d97706', background: '#fff5f0', padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
            <ClockO size={14} /> 候补排队中
          </div>
        );
      case 'cancelled':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8', background: '#f1f5f9', padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
            <Close size={14} /> 已取消
          </div>
        );
      default:
        return <Tag size="medium">{status}</Tag>;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: '"Inter", "PingFang SC", "Microsoft YaHei", sans-serif' }}>
      <div style={{ 
          background: 'rgba(255, 255, 255, 0.85)', 
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          position: 'sticky', 
          top: 0, 
          zIndex: 100,
          borderBottom: '1px solid rgba(0,0,0,0.03)'
      }}>
          <NavBar 
            title={<span style={{ fontWeight: 800, fontSize: 18, color: '#0f172a' }}>我的报名</span>} 
            leftText={<ArrowLeft size={20} color="#0f172a" />} 
            onClickLeft={() => navigate(-1)} 
            border={false}
            style={{ background: 'transparent' }}
          />
      </div>
      
      <PullRefresh onRefresh={fetchMyRegistrations}>
        {loading ? (
          <div style={{ padding: '20px' }}>
            <div style={{ background: '#fff', padding: 20, borderRadius: 20, marginBottom: 16 }}><Skeleton title row={3} /></div>
            <div style={{ background: '#fff', padding: 20, borderRadius: 20 }}><Skeleton title row={3} /></div>
          </div>
        ) : (
          <div style={{ padding: '16px' }}>
            {registrations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', background: '#fff', borderRadius: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🗂️</div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>暂无报名记录</div>
              </div>
            ) : (
              registrations.map(reg => (
                <div key={reg.id} style={{ 
                  background: '#fff', 
                  borderRadius: '20px', 
                  padding: '20px',
                  marginBottom: '16px',
                  position: 'relative',
                  boxShadow: '0 8px 24px rgba(149, 157, 165, 0.08)',
                  border: '1px solid rgba(255,255,255,0.8)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 4, height: 16, background: '#056b4f', borderRadius: 2 }}></div>
                      {reg.club_name}
                    </div>
                    {renderStatus(reg.status)}
                  </div>
                  
                  <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.8', background: '#f8fafc', padding: '12px', borderRadius: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#94a3b8' }}>学生姓名：</span>
                      <span style={{ fontWeight: 600, color: '#0f172a' }}>{reg.student_name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#94a3b8' }}>报名时间：</span>
                      <span>{dayjs(reg.registered_at).format('YYYY-MM-DD HH:mm:ss')}</span>
                    </div>
                    {reg.status === 'waitlisted' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#e11d48', marginTop: 4, fontWeight: 600 }}>
                        <LabelO />当前排队位置：第 {reg.queue_position} 位
                      </div>
                    )}
                  </div>
                  
                  {reg.status !== 'cancelled' && (
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                      <div 
                        style={{ 
                          color: '#e11d48', 
                          fontSize: '14px', 
                          fontWeight: 600,
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 4,
                          padding: '6px 16px', 
                          background: '#fff0f2',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onClick={() => handleCancel(reg.id)}
                      >
                        <Cross size={14} /> 取消报名
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </PullRefresh>
    </div>
  );
};

export default MyRegistrations;
